import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ModuleRequestError, describeModuleError } from '@wit-support/admin-client-core';
import { Fragment } from 'react';
import { useQuery } from '@tanstack/react-query';
import { notConfiguredText } from '@wit-support/ui';
import { ModuleScreen, PageToolbar, usePageChromeMode, } from '@wit-support/ui-domain';
import { FakeStandBanner } from './components/fake-stand-banner.js';
import { adsErrorText, getAdsHealth, getAdsRegistry, listAccounts, listCountryLists, listLocales, } from './api.js';
import { ADS_QUERY_DEFAULTS, REFERENCE_STALE_MS } from './query-policy.js';
// --- ключи запросов ---------------------------------------------------------
/** Корень всех ключей раздела: одна инвалидация после мутации обновляет все экраны. */
export const ADS_KEY = ['ads'];
export const HEALTH_KEY = ['ads', 'health'];
export const REGISTRY_KEY = ['ads', 'registry'];
export const NAMING_SAMPLE_KEY = ['ads', 'registry', 'naming-sample'];
export const ACCOUNTS_KEY = ['ads', 'accounts'];
export const accountKey = (id) => ['ads', 'account', id];
export const TEMPLATES_KEY = ['ads', 'templates'];
export const templateKey = (id) => ['ads', 'template', id];
export const BATCHES_KEY = ['ads', 'batches'];
export const batchKey = (id) => ['ads', 'batch', id];
export const launchKey = (id) => ['ads', 'launch', id];
export const MEDIA_KEY = ['ads', 'media'];
export const LOCALES_KEY = ['ads', 'locales'];
export const COUNTRY_LISTS_KEY = ['ads', 'country-lists'];
// --- тексты ошибок ----------------------------------------------------------
const SERVICE = 'Сервис рекламы auto-ads (конструктор запусков)';
const ENV = ['AUTO_ADS_API_BASE_URL', 'AUTO_ADS_API_TOKEN'];
export const NOT_CONFIGURED_TEXT = notConfiguredText(SERVICE, ENV);
export const UNAVAILABLE_TEXT = 'Сервис рекламы недоступен, попробуйте позже';
export const BUILDER_DISABLED_TEXT = 'Конструктор запусков выключен на сервере (META_ADS_BUILDER_ENABLED). Обратитесь к администратору.';
export const FORBIDDEN_READ_TEXT = 'Раздел «Реклама» недоступен вашей роли: его видят супервайзеры и владелец.';
export const FORBIDDEN_WRITE_TEXT = 'Это действие недоступно вашей роли.';
/**
 * Ошибку — словами (спека §11). `mode: 'write'` — для мутаций: 403 там
 * значит «смотреть можно, менять нельзя». Отказ auto-ads (`upstream_rejected`)
 * admin-api присылает русской фразой в `message` — она и показывается.
 */
export function describeAdsError(err, mode = 'read') {
    return describeModuleError(err, mode, {
        forbiddenRead: FORBIDDEN_READ_TEXT,
        forbiddenWrite: FORBIDDEN_WRITE_TEXT,
        notConfiguredCode: 'ads_not_configured',
        notConfiguredText: NOT_CONFIGURED_TEXT,
        unavailableText: UNAVAILABLE_TEXT,
        textOf: adsErrorText,
        extra: (e) => {
            if (e.code === 'ads_builder_disabled')
                return BUILDER_DISABLED_TEXT;
            if (e.upstreamCode === 'rate-limited') {
                const sec = Number(e.details.retryAfterSec);
                return Number.isFinite(sec) && sec > 0
                    ? `Meta ограничила запросы, повторим через ${Math.ceil(sec)} с`
                    : 'Meta ограничила запросы, повторим чуть позже';
            }
            return null;
        },
    });
}
/**
 * Подробности ошибки Meta (502 `meta-error`): `userMsg` / `fbtraceId`, если
 * шлюз их передал. Пусто — блок не рисуется.
 */
export function metaErrorDetails(err) {
    if (!(err instanceof ModuleRequestError))
        return { userMsg: null, fbtraceId: null };
    const pick = (k) => typeof err.details[k] === 'string' ? err.details[k] : null;
    return { userMsg: pick('userMsg') ?? pick('metaUserMsg'), fbtraceId: pick('fbtraceId') };
}
export function isUpstreamCode(err, code) {
    return err instanceof ModuleRequestError && err.upstreamCode === code;
}
/**
 * Сабмит формы через `mutateAsync`: `ZodForm` ждёт промис, поэтому кнопка
 * заблокирована на время запроса. Ошибку уже показал `onError` мутации.
 */
export async function submitting(p) {
    await p.catch(() => undefined);
}
// --- общие запросы ----------------------------------------------------------
export function useAdsHealth() {
    return useQuery({ ...ADS_QUERY_DEFAULTS, queryKey: HEALTH_KEY, queryFn: getAdsHealth });
}
/** Реестр полей меняется только с выкатом auto-ads — кэшируем надолго. */
/** Служебная ссылка на решения в тексте реестра: «(Q6)», «(Q6, Q7)», «(Р 48)», «(D14)». */
const SERVICE_CODE_RE = /\s*\((?:Q|D|R|Р|В)\s?\d+(?:\s*[,;]\s*(?:Q|D|R|Р|В)\s?\d+)*\)/g;
/** Подсказка реестра без служебных кодов решений (E-2 P3: «Выключена по умолчанию (Q6)»). */
export function hintWithoutCodes(hint) {
    if (hint == null)
        return null;
    const out = hint.replace(SERVICE_CODE_RE, '').trim();
    return out === '' ? null : out;
}
/** Реестр для показа: подсказки полей и вариантов — без служебных кодов. */
export function humanRegistry(r) {
    const descs = (list) => list.map((d) => ({
        ...d,
        hint: hintWithoutCodes(d.hint),
        options: d.options?.map((o) => ({ ...o, hint: hintWithoutCodes(o.hint) })) ?? d.options,
    }));
    return { ...r, fields: descs(r.fields), options: descs(r.options) };
}
/**
 * Реестр auto-ads (справочник). `select: humanRegistry` — подсказки полей и
 * вариантов без служебных кодов решений («(Q6)», E-2 P3) для ВСЕХ экранов;
 * в кэше запроса лежит ответ сервера как есть.
 */
export function useRegistry() {
    return useQuery({
        ...ADS_QUERY_DEFAULTS,
        queryKey: REGISTRY_KEY,
        queryFn: getAdsRegistry,
        staleTime: REFERENCE_STALE_MS,
        select: humanRegistry,
    });
}
/** Кабинеты — справочник: меняются редко, свои правки сбрасывают кэш (`ADS_KEY`). */
export function useAccounts() {
    return useQuery({
        ...ADS_QUERY_DEFAULTS,
        queryKey: ACCOUNTS_KEY,
        queryFn: listAccounts,
        staleTime: REFERENCE_STALE_MS,
    });
}
export function useLocales() {
    return useQuery({
        ...ADS_QUERY_DEFAULTS,
        queryKey: LOCALES_KEY,
        queryFn: listLocales,
        staleTime: 10 * 60_000,
    });
}
export function useCountryLists() {
    return useQuery({
        ...ADS_QUERY_DEFAULTS,
        queryKey: COUNTRY_LISTS_KEY,
        queryFn: listCountryLists,
        staleTime: REFERENCE_STALE_MS,
    });
}
/**
 * Каркас экрана раздела (`ModuleScreen` из ui-domain): заголовок + проверка
 * «auto-ads подключён и конструктор включён». На стенде с fake-Meta сверху —
 * баннер «Тестовый стенд».
 *
 * Режим `'shell'` (`routeChrome` модуля): заголовок — крошка, описание — ⓘ,
 * кнопки — `PageToolbar` в навбаре с приоритетами. Тулбар стоит в теле
 * `ModuleScreen`, а тело монтируется только когда раздел может работать —
 * так кнопки не появляются над «сервис не подключён».
 */
export function AdsScreen({ title, description, toolbar, children, width = 'wide', }) {
    const health = useAdsHealth();
    const shell = usePageChromeMode() === 'shell';
    const actions = toolbar && toolbar.length > 0 ? toolbar : null;
    return (_jsxs(ModuleScreen, { title: title, description: description, actions: !shell && actions ? (_jsx("div", { className: "flex flex-wrap gap-2", children: actions.map((a) => (_jsx(Fragment, { children: a.render('md') }, a.label))) })) : null, health: health, service: SERVICE, envVars: ENV, describeError: describeAdsError, unavailableText: UNAVAILABLE_TEXT, disabledText: BUILDER_DISABLED_TEXT, banner: health.data?.fakeMeta === true ? _jsx(FakeStandBanner, {}) : null, width: !shell && width === 'full' ? 'wide' : width, children: [shell && actions ? (_jsx(PageToolbar, { "aria-label": "\u0414\u0435\u0439\u0441\u0442\u0432\u0438\u044F \u0440\u0430\u0437\u0434\u0435\u043B\u0430", children: actions.map((a) => (_jsx(PageToolbar.Item, { label: a.label, priority: a.priority ?? 'primary', children: a.render('sm') }, a.label))) })) : null, children] }));
}
//# sourceMappingURL=shared.js.map